import { FC, ChangeEvent, ClipboardEvent } from 'react';

import { OTP_LENGTH } from 'utils/constants';
import { cn } from 'utils/helpers';

import CharField from './CharField';

interface PROPS {
  length?: number;
  className?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  success?: boolean;
}

const OTPInput: FC<PROPS> = ({
  length = OTP_LENGTH,
  className,
  value,
  onChange,
  error,
  success,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const k = e.key;

    const isDigit =
      (k >= '0' && k <= '9') ||
      (e.code?.startsWith('Numpad') && k !== 'NumpadEnter');
    const navKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Home',
      'End',
    ];
    const withMod = e.ctrlKey || e.metaKey;
    const modKeys = ['a', 'c', 'v', 'x'];

    if (isDigit) return;
    if (navKeys.includes(k)) return;
    if (withMod && modKeys.includes(k.toLowerCase())) return;

    e.preventDefault();
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const digits = (pasted.match(/\d/g) || []).join('').slice(0, length);
    const target = e.target as HTMLInputElement;

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    )?.set;
    nativeInputValueSetter?.call(target, digits);
    target.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, length);
    if (cleaned !== e.target.value) {
      e.target.value = cleaned;
    }
    onChange(e);
  };

  return (
    <div>
      <div className={cn('h-20 relative w-[352px]', className)}>
        <input
          className={cn('opacity-0', 'w-full h-full', 'absolute z-[99]')}
          type="text"
          name="otp"
          value={value}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onPaste={handlePaste}
          autoComplete="one-time-code"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label="One-time password"
        />
        <CharField
          length={length}
          value={value}
          error={error}
          success={success}
        />
      </div>
      <p className="text-danger-base text-center text-xs">
        {error && 'Wrong or expired OTP. Please try again.'}
      </p>
    </div>
  );
};

export default OTPInput;
